/**
 * 더존 G20 분개장 → ERP 전표 INSERT SQL (aslpmst / aslpdtl / aslpviw)
 *
 * 시흥산업진흥원 ENT_CD=200, 대상연도 2013~2022.
 * aslpviw 계층(LV1~LV5, ACC_UPP_CD)은 aaccmst 내부코드(ACC_UPP_CD)를 분해한다.
 * 원자료는 메모리 스토어만 쓰고, 그리드에는 화면 분량만 넘긴다.
 */
import * as XLSX from 'xlsx';
import aaccmstEnt200 from './datasets/aaccmstEnt200.js';

export const DEFAULT_PAGE_SIZE = 200;
export const FIRST_PAINT_ROWS = 80;
export const PARSE_CHUNK = 1500;
export const CONVERT_CHUNK = 2000;
export const SQL_BATCH = 400;
export const MIG_ENT_CD = '200';
export const MIG_YEAR_FROM = 2013;
export const MIG_YEAR_TO = 2022;

export const PAGE_SIZE_OPTIONS = [
  { value: '100', label: '100건' },
  { value: '200', label: '200건' },
  { value: '500', label: '500건' },
  { value: '1000', label: '1,000건' },
];

/** G20 분개장 헤더 → 표준필드 */
export const G20_JOURNAL_FIELDS = [
  { value: 'slipDate', label: '전표일자', aliases: ['전표일자', '일자', '작성일자', '회계일자', '전표일', '년월일', '년/월/일', 'fill_dt', 'isu_dt', 'filldt', 'isudt'] },
  { value: 'slipNo', label: '전표번호', aliases: ['전표번호', '승인번호', '전표no', '전표no.', 'appr_no', 'isu_sq', 'fill_nb', 'isusq'] },
  { value: 'lineNo', label: '라인', aliases: ['라인', '행번', '순번', '전표순번', 'ln_sq', 'lnsq', 'line'] },
  { value: 'slipType', label: '전표유형', aliases: ['전표유형', '전표구분', '전표종류', 'isu_ty', 'isuty'] },
  { value: 'acctCd', label: '계정코드', aliases: ['계정코드', '계정과목코드', 'acct_cd', 'acctcd', '과목코드'] },
  { value: 'acctNm', label: '계정명', aliases: ['계정명', '계정과목명', '계정과목', 'acct_nm', 'acctnm', '과목명'] },
  { value: 'drAmt', label: '차변', aliases: ['차변', '차변금액', '차변액', 'dr_amt', 'dramt', '차변합계'] },
  { value: 'crAmt', label: '대변', aliases: ['대변', '대변금액', '대변액', 'cr_amt', 'cramt', '대변합계'] },
  { value: 'dcType', label: '차대구분', aliases: ['차대구분', '차대', '대차구분', 'drcr', 'dc', 'drcr_fg', 'drcrfg'] },
  { value: 'amount', label: '금액', aliases: ['금액', 'amt', '발생액', 'acct_am', 'acctam'] },
  { value: 'remark', label: '적요', aliases: ['적요', '적요란', '적요내용', 'rmk', 'rmk_dc', 'rmkdc'] },
  { value: 'vendorCd', label: '거래처코드', aliases: ['거래처코드', 'tr_cd', 'trcd', 'biz_no', 'bizno', '사업자번호'] },
  { value: 'vendorNm', label: '거래처', aliases: ['거래처', '거래처명', 'tr_nm', 'trnm'] },
  { value: 'deptCd', label: '부서코드', aliases: ['부서코드', 'dept_cd', 'deptcd', 'dept_cd_h'] },
  { value: 'deptNm', label: '부서', aliases: ['부서', '부서명', 'dept_nm', 'deptnm'] },
  { value: 'projectCd', label: '사업코드', aliases: ['사업코드', '프로젝트코드', '프로젝트', 'mgt_cd', 'mgtcd', 'pjt_cd', 'pjtcd'] },
  { value: 'projectNm', label: '사업명', aliases: ['사업명', '프로젝트명', 'mgt_nm', 'mgtnm', 'pjt_nm', 'pjtnm'] },
  { value: 'bgtCd', label: '예산코드', aliases: ['예산코드', '예산과목코드', 'bgt_cd', 'bgtcd'] },
  { value: 'bgtNm', label: '예산과목', aliases: ['예산과목', '예산과목명', 'bgt_nm', 'bgtnm'] },
  { value: 'resolveNo', label: '결의번호', aliases: ['결의번호', 'gisu_no', 'gisuno'] },
  { value: 'causeNo', label: '원인행위번호', aliases: ['원인행위번호', '원인행위', 'cause_no', 'causeno'] },
  { value: 'evidType', label: '증빙', aliases: ['증빙', '증빙구분', '증빙유형'] },
  { value: 'acctUnit', label: '회계단위', aliases: ['회계단위', 'co_cd', 'cocd'] },
  { value: 'grp', label: 'GRP', aliases: ['grp'] },
];


const store = {
  tenantCode: '',
  fileName: '',
  loadedAt: '',
  sourceRows: [],
  acctMap: new Map(),
  accMaster: new Map(),
  sourceColDefs: [],
  parseWarnings: [],
  skipped: 0,
  sheetName: '',
  mapSheet: '',
  accSheet: '',
  accMasterSource: '',
};

export function getJournalStore() {
  return store;
}

export function clearJournalStore() {
  store.fileName = '';
  store.loadedAt = '';
  store.sourceRows = [];
  store.acctMap = new Map();
  store.accMaster = new Map();
  store.sourceColDefs = [];
  store.parseWarnings = [];
  store.skipped = 0;
  store.sheetName = '';
  store.mapSheet = '';
  store.accSheet = '';
  store.accMasterSource = '';
}

export function yieldToUi() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    } else {
      setTimeout(resolve, 0);
    }
  });
}

export function normalizeHeader(h) {
  return String(h || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[()[\]{}._\-/·]/g, '');
}

function cellStr(v) {
  if (v === null || v === undefined) return '';
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    const y = v.getFullYear();
    const m = String(v.getMonth() + 1).padStart(2, '0');
    const d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  return String(v).trim();
}

export function toAmt(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
  const n = Number(String(v).replace(/,/g, '').replace(/원/g, '').replace(/\s/g, ''));
  return Number.isFinite(n) ? Math.trunc(n) : 0;
}

export function toDateYmd(v) {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  if (typeof v === 'number' && v > 20000 && v < 90000 && typeof XLSX.SSF?.parse_date_code === 'function') {
    const d = XLSX.SSF.parse_date_code(v);
    if (d?.y) return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
  }
  const s = String(v).trim();
  const m = s.match(/^(\d{4})[./\-년]?[.\-\s/]?(\d{1,2})[./\-월]?[.\-\s/]?(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  const compact = s.replace(/\D/g, '');
  if (compact.length === 8) {
    return `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`;
  }
  return '';
}

function matchField(header) {
  const key = normalizeHeader(header);
  if (!key) return '';
  for (const field of G20_JOURNAL_FIELDS) {
    if (field.aliases.some((a) => normalizeHeader(a) === key)) return field.value;
  }
  return '';
}

export function findHeaderRow(aoa) {
  const rows = aoa || [];
  const limit = Math.min(rows.length, 40);
  let best = { index: -1, score: 0, headers: [] };
  for (let i = 0; i < limit; i += 1) {
    const headers = (rows[i] || []).map((c) => cellStr(c));
    if (!headers.some((h) => h)) continue;
    const score = headers.reduce((acc, h) => acc + (matchField(h) ? 1 : 0), 0);
    if (score > best.score) best = { index: i, score, headers };
  }
  if (best.score >= 2) return best;
  for (let i = 0; i < rows.length; i += 1) {
    const headers = (rows[i] || []).map((c) => cellStr(c));
    if (headers.some((h) => h)) return { index: i, score: 0, headers };
  }
  return { index: 0, score: 0, headers: [] };
}

function readWorkbook(buf) {
  return XLSX.read(buf, {
    type: 'array',
    dense: true,
    cellDates: true,
    raw: false,
  });
}

function sheetToAoa(wb, name) {
  const ws = wb.Sheets[name];
  if (!ws) return [];
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
}

function pickJournalSheet(wb) {
  const names = wb.SheetNames || [];
  const prefer = names.find((n) => /분개|journal|slip|전표/i.test(String(n)));
  return prefer || names[0];
}

function pickMapSheet(wb) {
  const names = wb.SheetNames || [];
  return names.find((n) => /계정매핑|account.?map|acct.?map|mapping/i.test(String(n))) || '';
}

function pickAccSheet(wb) {
  const names = wb.SheetNames || [];
  return names.find((n) => /계정마스터|계정과목마스터|aaccmst|accmst/i.test(String(n))) || '';
}

function isAccMasterAoa(aoa) {
  const keys = (aoa?.[0] || []).map((c) => normalizeHeader(cellStr(c)));
  if (keys.includes('내부코드') && keys.includes('계정코드')) return true;
  if (keys.includes('accuppcd') && keys.includes('acccd')) return true;
  if (keys.includes('결산계정명') && keys.includes('계정코드') && !keys.includes('구분기표') && !keys.includes('ln_sq') && !keys.includes('lnsq')) {
    return true;
  }
  return false;
}

function isAccMasterWorkbook(wb) {
  const name = (wb.SheetNames || [])[0];
  if (!name) return false;
  if (/aaccmst|계정마스터|계정과목마스터/i.test(String(name))) return true;
  return isAccMasterAoa(sheetToAoa(wb, name));
}

export function accMasterFromList(list) {
  const map = new Map();
  for (const r of list || []) {
    const cd = String(r.ACC_CD || '').trim();
    if (!cd) continue;
    map.set(cd, {
      ACC_CD: cd,
      ACC_UPP_CD: String(r.ACC_UPP_CD || '').trim(),
      ACC_DCR_GB: String(r.ACC_DCR_GB || '1').trim() || '1',
      ACC_NM: String(r.ACC_NM || '').trim(),
    });
  }
  return map;
}

export function loadDefaultAccMaster() {
  return accMasterFromList(aaccmstEnt200);
}

export function applyAccMasterMap(map, sourceName) {
  store.accMaster = map instanceof Map ? map : new Map();
  store.accMasterSource = sourceName || store.accMasterSource || 'aaccmst';
  return store.accMaster.size;
}

function parseAcctMap(aoa) {
  const map = new Map();
  if (!aoa?.length) return map;
  const { index, headers } = findHeaderRow(aoa);
  const idx = {};
  headers.forEach((h, i) => {
    const k = normalizeHeader(h);
    if (['g20계정', 'g20계정코드', '계정코드', 'acctcd', 'from', '원계정'].includes(k) && idx.src == null) idx.src = i;
    if (['erp계정', 'erp계정코드', '변환계정', 'to', 'toacct'].includes(k)) idx.toCd = i;
    if (['erp계정명', '변환계정명', '계정명', 'acctnm'].includes(k) && idx.toNm == null) idx.toNm = i;
    if (['fstype', '재무제표구분', '제표구분', '축'].includes(k)) idx.fsType = i;
    if (['fsline', '재무제표항목', '제표항목', '항목'].includes(k)) idx.fsLine = i;
  });
  if (idx.src == null) idx.src = 0;
  if (idx.toCd == null) idx.toCd = 1;
  for (let r = index + 1; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    const src = cellStr(row[idx.src]);
    if (!src) continue;
    map.set(src, {
      acctCd: cellStr(row[idx.toCd]) || src,
      acctNm: idx.toNm != null ? cellStr(row[idx.toNm]) : '',
      fsType: idx.fsType != null ? cellStr(row[idx.fsType]) : '',
      fsLine: idx.fsLine != null ? cellStr(row[idx.fsLine]) : '',
    });
  }
  return map;
}

function parseAccMaster(aoa) {
  const map = new Map();
  if (!aoa?.length) return map;
  let index = 0;
  let headers = (aoa[0] || []).map((c) => cellStr(c));
  const limit = Math.min(aoa.length, 15);
  for (let i = 0; i < limit; i += 1) {
    const hs = (aoa[i] || []).map((c) => cellStr(c));
    const keys = hs.map((h) => normalizeHeader(h));
    if (keys.includes('acccd') || keys.includes('계정코드')) {
      index = i;
      headers = hs;
      break;
    }
  }
  const idx = { cd: 0, upp: -1, dcr: -1, nm: -1 };
  headers.forEach((h, i) => {
    const k = normalizeHeader(h);
    if (['acccd', '계정코드', 'acc_cd'].includes(k)) idx.cd = i;
    if (['accuppcd', '상위계정', 'acc_upp_cd', '내부코드', '내부cd'].includes(k) || k.includes('upp')) idx.upp = i;
    if (['accdcrgb', '차대구분', 'acc_dcr_gb'].includes(k) || k.includes('dcr')) idx.dcr = i;
    if (['accnm', '계정명', 'acc_nm'].includes(k) && idx.nm < 0) idx.nm = i;
  });
  for (let r = index + 1; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    const cd = cellStr(row[idx.cd]);
    if (!cd) continue;
    map.set(cd, {
      ACC_CD: cd,
      ACC_UPP_CD: idx.upp >= 0 ? cellStr(row[idx.upp]) : '',
      ACC_DCR_GB: idx.dcr >= 0 ? cellStr(row[idx.dcr]) : '',
      ACC_NM: idx.nm >= 0 ? cellStr(row[idx.nm]) : '',
    });
  }
  fillMissingDcr(map);
  return map;
}

function inferDcrFromUpp(upp, nm) {
  const u = String(upp || '');
  const name = String(nm || '');
  if (/감가상각누계|국고보조금/.test(name) && u.startsWith('1')) return '2';
  if (u.startsWith('1')) return '1';
  if (u.startsWith('2')) return '2';
  if (u.startsWith('3')) {
    if (/결손/.test(name) || u.startsWith('350-76')) return '1';
    return '2';
  }
  if (u.startsWith('41') || u.startsWith('44')) return '2';
  if (u.startsWith('4')) return '1';
  return '1';
}

function fillMissingDcr(map) {
  const def = accMasterFromList(aaccmstEnt200);
  map.forEach((v, cd) => {
    if (v.ACC_DCR_GB) return;
    v.ACC_DCR_GB = def.get(cd)?.ACC_DCR_GB || inferDcrFromUpp(v.ACC_UPP_CD, v.ACC_NM);
  });
}

function sourceColSize(header) {
  const k = normalizeHeader(header);
  if (/적요|rmk|doc|명|memo/.test(k)) return 200;
  if (/금액|amt|차변|대변/.test(k)) return 110;
  if (/일자|ymd|date/.test(k)) return 120;
  return 100;
}

function aoaWidth(aoa) {
  let n = 0;
  for (const row of aoa || []) {
    if ((row || []).length > n) n = row.length;
  }
  return n;
}

function buildSourceColDefs(row0, row1, width) {
  let n = width ?? Math.max((row0 || []).length, (row1 || []).length);
  while (n > 0) {
    const a = cellStr((row0 || [])[n - 1]);
    const b = row1 ? cellStr((row1 || [])[n - 1]) : '';
    if (a || b) break;
    n -= 1;
  }
  const used = new Map();
  const defs = [];
  for (let i = 0; i < n; i += 1) {
    const a = cellStr((row0 || [])[i]);
    const b = row1 ? cellStr((row1 || [])[i]) : '';
    let header = '';
    if (a && b && normalizeHeader(a) !== normalizeHeader(b)) header = `${a} ${b}`;
    else header = b || a || `열${i + 1}`;
    header = String(header).replace(/\s+/g, ' ').trim();
    const nDup = used.get(header) || 0;
    used.set(header, nDup + 1);
    if (nDup) header = `${header} (${nDup + 1})`;
    defs.push({ key: `src${i}`, header, size: sourceColSize(header) });
  }
  return defs;
}

/** 더존 G20 분개장 출력(2단 헤더 + LN_SQ/DRCR_FG/ACCT_CD) */
function isG20PrintLayout(aoa) {
  const h0 = (aoa[0] || []).map((c) => normalizeHeader(cellStr(c)));
  const h1 = (aoa[1] || []).map((c) => normalizeHeader(cellStr(c)));
  return h0.includes('구분기표') && h0.includes('acctcd') && h0.includes('drcrfg') && h1.includes('년월일');
}

const G20_PRINT_DUMP = {
  lnsq: 'lineNo',
  drcrfg: 'dcType',
  acctcd: 'acctCd',
  acctnm: 'acctNm',
  acctam: 'amount',
  deptcd: 'deptCd',
  deptnm: 'deptNm',
  rmkdck: 'remark',
  rmkdc: 'remark',
  trnmk: 'vendorNm',
  pjtnmk: 'projectNm',
  isudoc: 'isuDoc',
  isudock: 'isuDoc',
  taxfg: 'taxFg',
  taxnm: 'taxNm',
  supam: 'supAm',
  vatam: 'vatAm',
};

/** 시흥 G20 분개장 출력 양식 1행(그룹) — 2013.xls 이름 있는 76열 */
const G20_PRINT_HEADER_ROW0 = [
  '구 분[기표]', '구 분[기표]', '차     변', '차     변', '대    변', '대    변', '적요', '거래처', '거래처',
  '구 분[결의]', '구 분[결의]', 'PJT', 'PJT',
  'LN_SQ', 'DRCR_FG', 'ACCT_CD', 'ACCT_NM', 'ACCT_NMK', 'TR_NMK', 'RMK_DCK', 'ACCT_AM',
  'DEPT_CD', 'DEPT_NM', 'DEPT_NMK', 'ATTR_NM', 'ATTR_NMK', 'RMK_NB',
  '결의부서', '결의부서', 'DEPT_NM_HK', '결의사원', '결의사원', 'KOR_NMK',
  '사업장', '사업장', 'DIV_NMK', 'PJT_NMK',
  '사용부서', '사용부서', 'DEPT_NM_DK', '사용사원', '사용사원', 'EMP_NM_DK',
  '자금항목', '자금항목', 'CASH_NMK', '유형번호', '유형번호', 'CTD_NMK',
  '증빙구분', '증빙구분', 'CTD_NM_ATK',
  'INSERT_ID', '입력일자', 'INSERT_IP', 'MODIFY_ID', 'MODIFY_DT', 'MODIFY_IP',
  '사업자번호', 'ISU_DOC', 'ISU_DOCK', 'MEMO_CD', 'CHECK_PEN',
  'TAX_FG', 'TAX_NM', 'SUP_AM', 'VAT_AM', 'CT_DEAL', 'EX_TY',
  '거래처구분코드', '거래처구분명', 'TRGRP_NMK', 'PJT분류코드', 'PJT분류명', 'PJTGRP_NMK', 'CHK_ALT',
];

/** 시흥 G20 분개장 출력 양식 2행 */
const G20_PRINT_HEADER_ROW1 = [
  '년/월/일', '번호', '금액', '계정과목', '계정과목', '금액', '적요', '코드', '거래처명',
  '년/월/일', '번호', '코드', 'PJT명',
  'LN_SQ', 'DRCR_FG', 'ACCT_CD', 'ACCT_NM', 'ACCT_NMK', 'TR_NMK', 'RMK_DCK', 'ACCT_AM',
  'DEPT_CD', 'DEPT_NM', 'DEPT_NMK', 'ATTR_NM', 'ATTR_NMK', 'RMK_NB',
  '코드', '부서명', 'DEPT_NM_HK', '코드', '사원명', 'KOR_NMK',
  '코드', '사업장명', 'DIV_NMK', 'PJT_NMK',
  '코드', '부서명', 'DEPT_NM_DK', '코드', '사원명', 'EMP_NM_DK',
  '코드', '자금항목명', 'CASH_NMK', '번호', '유형명', 'CTD_NMK',
  '구분', '구분명', 'CTD_NM_ATK',
  'INSERT_ID', '입력일자', 'INSERT_IP', 'MODIFY_ID', 'MODIFY_DT', 'MODIFY_IP',
  '사업자번호', 'ISU_DOC', 'ISU_DOCK', 'MEMO_CD', 'CHECK_PEN',
  'TAX_FG', 'TAX_NM', 'SUP_AM', 'VAT_AM', 'CT_DEAL', 'EX_TY',
  '거래처구분코드', '거래처구분명', 'TRGRP_NMK', 'PJT분류코드', 'PJT분류명', 'PJTGRP_NMK', 'CHK_ALT',
];

function padPrintSample(cells) {
  const n = G20_PRINT_HEADER_ROW0.length;
  const row = (cells || []).slice();
  while (row.length < n) row.push('');
  return row.slice(0, n);
}

const ACC_UNT = '1';
const SLP_GB = '1';
const INS_USR = 'G20_MIG';
const GB_DANGHAE = '1';
const CASH_ACS = new Set(['10300', '10500']);
const ACC_ALIAS = { '82003': '82000', '82001': '82000', '10301': '10300' };
const ACC_FALLBACK = { '96000': { upp: '450-60', dcr: '1' } };

function nthHeader(headers, pred, n = 0) {
  let seen = -1;
  for (let i = 0; i < (headers || []).length; i += 1) {
    if (!pred(headers[i])) continue;
    seen += 1;
    if (seen === n) return i;
  }
  return null;
}

function buildG20PrintColIndex(row0, row1) {
  const col = {};
  const scan = (headers) => {
    (headers || []).forEach((h, i) => {
      const field = G20_PRINT_DUMP[normalizeHeader(cellStr(h))];
      if (field && col[field] == null) col[field] = i;
    });
  };
  scan(row0);
  scan(row1);
  const h1 = (row1 || []).map((c) => normalizeHeader(cellStr(c)));
  if (col.slipDate == null) col.slipDate = nthHeader(h1, (k) => k === '년월일', 0);
  if (col.slipNo == null) col.slipNo = nthHeader(h1, (k) => k === '번호', 0);
  if (col.resolveNo == null) col.resolveNo = nthHeader(h1, (k) => k === '번호', 1);
  if (col.vendorCd == null) col.vendorCd = nthHeader(h1, (k) => k === '코드', 0);
  if (col.projectCd == null) col.projectCd = nthHeader(h1, (k) => k === '코드', 1);
  if (col.deptCdH == null) col.deptCdH = nthHeader(h1, (k) => k === '코드', 2);
  if (col.empCd == null) col.empCd = nthHeader(h1, (k) => k === '코드', 3);
  if (col.bizNo == null) col.bizNo = nthHeader(h1, (k) => k === '사업자번호', 0);
  if (col.vendorNm == null) col.vendorNm = nthHeader(h1, (k) => k === '거래처명' || k === '거래처', 0);
  const pjtNm = nthHeader(h1, (k) => k === 'pjt명' || k === 'pjtnm', 0);
  if (pjtNm != null) col.projectNm = pjtNm;
  const deptNmKor = nthHeader(h1, (k) => k === '부서명', 0);
  if (deptNmKor != null) col.deptNm = deptNmKor;
  if (col.remark == null) col.remark = nthHeader(h1, (k) => k === '적요', 0);
  return col;
}

function buildColIndex(headers) {
  const col = {};
  // dump 컬럼(ACCT_AM 등)을 한글 '금액'·'차변'보다 우선
  (headers || []).forEach((h, i) => {
    const field = G20_PRINT_DUMP[normalizeHeader(cellStr(h))];
    if (field && col[field] == null) col[field] = i;
  });
  (headers || []).forEach((h, i) => {
    const field = matchField(h);
    if (field && col[field] == null) col[field] = i;
  });
  return col;
}

function splitAcct(code, name) {
  const c = cellStr(code);
  const n = cellStr(name);
  if (c && n) return { acctCd: c, acctNm: n };
  const blob = c || n;
  const m = blob.match(/^(\d{3,})\s+(.+)$/);
  if (m) return { acctCd: m[1], acctNm: m[2] };
  if (/^\d{3,}$/.test(blob)) return { acctCd: blob, acctNm: n && n !== blob ? n : '' };
  return { acctCd: c, acctNm: n };
}

function isSummaryRow(raw, remark, grp) {
  const text = `${remark || ''} ${cellStr(raw)}`;
  if (/월\s*계|누\s*계|합\s*계|총\s*계|전기이월|차기이월/.test(text)) return true;
  const g = cellStr(grp);
  return g === '3' || g === '4';
}

/** G20 엑셀은 병합셀로 전표일자·번호가 전표 첫 행에만 있는 경우가 많다 */
function forwardFillSlips(rows) {
  let prev = null;
  for (const row of rows || []) {
    if (prev) {
      if (!row.slipDate) row.slipDate = prev.slipDate;
      if (!row.slipNo) row.slipNo = prev.slipNo;
      if (!row.acctUnit) row.acctUnit = prev.acctUnit;
      if (!row.slipType) row.slipType = prev.slipType;
    }
    prev = row;
  }
  return rows;
}

function applyDcAmount(dcType, amount, drAmt, crAmt) {
  let dr = toAmt(drAmt);
  let cr = toAmt(crAmt);
  if (dr || cr) return { drAmt: dr, crAmt: cr };
  const amt = toAmt(amount);
  if (!amt) return { drAmt: 0, crAmt: 0 };
  const t = normalizeHeader(dcType);
  if (/^4$|대변|^대$|cr|credit/.test(t)) return { drAmt: 0, crAmt: amt };
  return { drAmt: amt, crAmt: 0 };
}

/**
 * 공익법인 재무제표 축 기본값 — 계정코드 선두 숫자.
 * 계정매핑 시트가 있으면 그 값이 이 규칙을 덮어쓴다.
 */
export function classifyFs(acctCd, mapped) {
  if (mapped?.fsType || mapped?.fsLine) {
    const fsType = mapped.fsType || inferFsType(acctCd);
    return {
      fsType,
      fsStmt: fsStmtOf(fsType),
      fsLine: mapped.fsLine || defaultFsLine(fsType),
    };
  }
  const fsType = inferFsType(acctCd);
  return { fsType, fsStmt: fsStmtOf(fsType), fsLine: defaultFsLine(fsType) };
}

function inferFsType(acctCd) {
  const d = String(acctCd || '').replace(/\D/g, '').charAt(0);
  if (d === '1') return 'ASSET';
  if (d === '2') return 'LIABILITY';
  if (d === '3') return 'NET_ASSET';
  if (d === '4') return 'REVENUE';
  if (d === '5' || d === '6' || d === '7') return 'EXPENSE';
  return 'OTHER';
}

function fsStmtOf(fsType) {
  if (fsType === 'REVENUE' || fsType === 'EXPENSE') return 'OE';
  if (fsType === 'ASSET' || fsType === 'LIABILITY' || fsType === 'NET_ASSET') return 'BS';
  return '';
}

function defaultFsLine(fsType) {
  switch (fsType) {
    case 'ASSET':
      return '자산';
    case 'LIABILITY':
      return '부채';
    case 'NET_ASSET':
      return '순자산';
    case 'REVENUE':
      return '사업수익';
    case 'EXPENSE':
      return '사업비용';
    default:
      return '';
  }
}

function fsTypeLabel(fsType) {
  switch (fsType) {
    case 'ASSET':
      return '자산';
    case 'LIABILITY':
      return '부채';
    case 'NET_ASSET':
      return '순자산';
    case 'REVENUE':
      return '수익';
    case 'EXPENSE':
      return '비용';
    default:
      return '기타';
  }
}

export async function parseG20JournalWorkbook(buf, { tenantCode, fileName, onProgress, append } = {}) {
  const wb = readWorkbook(buf);
  if (isAccMasterWorkbook(wb)) {
    const name = wb.SheetNames[0];
    const map = parseAccMaster(sheetToAoa(wb, name));
    if (!map.size) throw new Error('계정마스터에서 계정코드를 읽지 못했습니다.');
    applyAccMasterMap(map, fileName || name);
    return {
      kind: 'accMaster',
      fileName: fileName || '',
      accCount: map.size,
      rowCount: 0,
      skipped: 0,
      warnings: [],
    };
  }
  const journalName = pickJournalSheet(wb);
  if (!journalName) throw new Error('엑셀 시트를 찾지 못했습니다.');
  const aoa = sheetToAoa(wb, journalName);
  const printLayout = isG20PrintLayout(aoa);
  let index;
  let headers;
  let score;
  let col;
  if (printLayout) {
    // 1행 그룹헤더 + 2행 년/월/일·번호. 데이터는 3행부터.
    index = 1;
    headers = (aoa[1] || []).map((c) => cellStr(c));
    col = buildG20PrintColIndex(aoa[0], aoa[1]);
    score = 99;
  } else {
    const found = findHeaderRow(aoa);
    index = found.index;
    headers = found.headers;
    score = found.score;
    col = buildColIndex(headers);
  }
  if (!headers.some((h) => String(h).trim())) {
    throw new Error('분개장 헤더 행을 찾지 못했습니다.');
  }
  if (col.slipDate == null && col.acctCd == null && col.acctNm == null && col.drAmt == null && col.amount == null) {
    throw new Error('분개장 컬럼(전표일자·계정·차대)을 인식하지 못했습니다. 양식을 확인하세요.');
  }

  const mapName = pickMapSheet(wb);
  const acctMap = mapName ? parseAcctMap(sheetToAoa(wb, mapName)) : new Map();
  const accSheet = pickAccSheet(wb);
  const accMaster = accSheet ? parseAccMaster(sheetToAoa(wb, accSheet)) : new Map();
  const sourceColDefs = printLayout
    ? buildSourceColDefs(aoa[0], aoa[1], aoaWidth(aoa))
    : buildSourceColDefs(headers, null, aoaWidth(aoa.slice(index)));

  const sourceRows = [];
  let skipped = 0;
  const warnings = [];
  if (!accMaster.size && !store.accMaster.size) {
    warnings.push('파일에 계정마스터가 없습니다. 변환 시 시흥 aaccmst(ENT_CD=200) 기본 마스터로 aslpviw LV를 채웁니다.');
  }

  const take = (row, field) => (col[field] == null ? '' : row[col[field]]);

  for (let r = index + 1; r < aoa.length; r += 1) {
    const row = aoa[r] || [];
    if (!(row || []).some((c) => cellStr(c) !== '')) continue;

    const remark = cellStr(take(row, 'remark'));
    const grp = take(row, 'grp');
    if (isSummaryRow(row.join?.(' ') ?? row, remark, grp)) {
      skipped += 1;
      continue;
    }

    const slipDate = toDateYmd(take(row, 'slipDate'));
    const { acctCd, acctNm } = splitAcct(take(row, 'acctCd'), take(row, 'acctNm'));
    const { drAmt, crAmt } = applyDcAmount(take(row, 'dcType'), take(row, 'amount'), take(row, 'drAmt'), take(row, 'crAmt'));
    const slipNo = cellStr(take(row, 'slipNo'));

    // 2단 헤더의 영문 컬럼명 행이 데이터로 섞이지 않게
    if (normalizeHeader(acctCd) === 'acctcd' || normalizeHeader(acctNm) === 'acctnm') {
      skipped += 1;
      continue;
    }

    if (!slipDate && !slipNo && !acctCd && !drAmt && !crAmt) {
      skipped += 1;
      continue;
    }

    const rec = {
      _rowUid: `G20-${fileName || 'f'}-${r + 1}`,
      srcRow: r + 1,
      tenantCode: tenantCode || '',
      slipDate,
      slipNo,
      lineNo: cellStr(take(row, 'lineNo')),
      slipType: cellStr(take(row, 'slipType')),
      dcType: cellStr(take(row, 'dcType')),
      acctCd,
      acctNm,
      drAmt,
      crAmt,
      remark,
      vendorCd: cellStr(take(row, 'vendorCd')),
      vendorNm: cellStr(take(row, 'vendorNm')),
      deptCd: cellStr(take(row, 'deptCdH')) || cellStr(take(row, 'deptCd')),
      deptNm: cellStr(take(row, 'deptNm')),
      empCd: cellStr(take(row, 'empCd')),
      projectCd: cellStr(take(row, 'projectCd')),
      projectNm: cellStr(take(row, 'projectNm')),
      bgtCd: cellStr(take(row, 'bgtCd')),
      bgtNm: cellStr(take(row, 'bgtNm')),
      resolveNo: cellStr(take(row, 'resolveNo')),
      causeNo: cellStr(take(row, 'causeNo')),
      evidType: cellStr(take(row, 'evidType')),
      acctUnit: cellStr(take(row, 'acctUnit')),
      isuDoc: cellStr(take(row, 'isuDoc')),
      bizNo: cellStr(take(row, 'bizNo')),
      taxFg: cellStr(take(row, 'taxFg')),
      taxNm: cellStr(take(row, 'taxNm')),
      supAm: toAmt(take(row, 'supAm')),
      vatAm: toAmt(take(row, 'vatAm')),
    };
    sourceColDefs.forEach((d, i) => {
      rec[d.key] = cellStr(row[i]);
    });
    sourceRows.push(rec);

    if (sourceRows.length % PARSE_CHUNK === 0) {
      onProgress?.({ loaded: sourceRows.length, skipped, totalHint: aoa.length - index - 1 });
      await yieldToUi();
    }
  }

  forwardFillSlips(sourceRows);

  store.tenantCode = tenantCode || store.tenantCode || MIG_ENT_CD;
  store.loadedAt = new Date().toISOString();
  if (append && store.sourceRows.length) {
    store.sourceRows = store.sourceRows.concat(sourceRows);
    store.skipped += skipped;
    const names = String(store.fileName || '')
      .split(' · ')
      .map((s) => s.trim())
      .filter(Boolean);
    if (fileName && !names.includes(fileName)) names.push(fileName);
    store.fileName = names.join(' · ');
    if (sourceColDefs.length > (store.sourceColDefs || []).length) store.sourceColDefs = sourceColDefs;
  } else {
    store.sourceRows = sourceRows;
    store.skipped = skipped;
    store.fileName = fileName || '';
    store.sourceColDefs = sourceColDefs;
  }
  if (acctMap.size) store.acctMap = acctMap;
  else if (!append) store.acctMap = acctMap;
  if (accMaster.size) {
    applyAccMasterMap(accMaster, fileName || accSheet);
    store.accSheet = accSheet || store.accSheet;
  }
  store.parseWarnings = [...(append ? store.parseWarnings : []), ...warnings];
  store.sheetName = journalName;
  store.mapSheet = mapName || store.mapSheet || '';

  return {
    kind: 'journal',
    fileName: store.fileName,
    sheetName: journalName,
    mapSheet: mapName,
    headers,
    rowCount: sourceRows.length,
    totalRowCount: store.sourceRows.length,
    skipped,
    mapCount: store.acctMap.size,
    accCount: store.accMaster.size,
    warnings,
  };
}

export function filterSourceRows(rows, values = {}) {
  const year = String(values.fiscalYear || '').trim();
  const from = toDateYmd(values.searchDateFrom);
  const to = toDateYmd(values.searchDateTo);
  const q = String(values.keyword || '').trim().toLowerCase();
  const yearFrom = values.yearFrom != null && values.yearFrom !== '' ? Number(values.yearFrom) : null;
  const yearTo = values.yearTo != null && values.yearTo !== '' ? Number(values.yearTo) : null;
  return (rows || []).filter((r) => {
    const yyyy = String(r.slipDate || '').slice(0, 4);
    if (year && yyyy !== year) return false;
    if (!year && yearFrom != null && Number.isFinite(yearFrom) && Number(yyyy) < yearFrom) return false;
    if (!year && yearTo != null && Number.isFinite(yearTo) && Number(yyyy) > yearTo) return false;
    if (from && String(r.slipDate || '') < from) return false;
    if (to && String(r.slipDate || '') > to) return false;
    if (q) {
      const blob = Object.keys(r)
        .filter((k) => k !== '_rowUid')
        .map((k) => r[k])
        .join(' ')
        .toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });
}

export function sliceForView(rows, pageNo, pageSize) {
  const size = Math.max(50, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const total = (rows || []).length;
  const totalPages = Math.max(1, Math.ceil(total / size) || 1);
  const page = Math.min(Math.max(1, Number(pageNo) || 1), totalPages);
  const start = (page - 1) * size;
  return {
    viewRows: (rows || []).slice(start, start + size),
    page,
    totalPages,
    start,
    end: Math.min(start + size, total),
    size,
    total,
  };
}

function padLineNo(n) {
  const v = Number(n);
  if (Number.isFinite(v) && v > 0) return v;
  return 0;
}

function emptyVal(v) {
  return v === null || v === undefined || String(v).trim() === '';
}

function ymdCompact(slipDate) {
  const s = toDateYmd(slipDate) || String(slipDate || '');
  return s.replace(/\D/g, '').slice(0, 8);
}

function bizNoDigits(v) {
  return String(v || '').replace(/[-\s]/g, '');
}

function isCashAcct(acctCd) {
  return CASH_ACS.has(String(acctCd || '').trim());
}

function slpTypOf(lines) {
  let cashDr = false;
  let cashCr = false;
  for (const r of lines) {
    if (!isCashAcct(r.acctCd)) continue;
    if (r.drAmt) cashDr = true;
    if (r.crAmt) cashCr = true;
  }
  if (cashCr && !cashDr) return '2';
  if (cashDr && !cashCr) return '1';
  return '3';
}

function dtlTypOf(acctCd) {
  const cd = String(acctCd || '').trim();
  if (isCashAcct(cd)) return '4';
  if (cd.startsWith('4') || cd.startsWith('9')) return '1';
  if (cd.startsWith('8')) return '2';
  return '3';
}

function accLevelsFromUpp(upp) {
  const s = String(upp || '').trim();
  const parts = s.split('-').filter(Boolean);
  const prefix = parts[0] || '';
  const base = { upp: s, lv1: prefix.slice(0, 1), lv2: prefix.slice(0, 2), lv3: prefix };
  // aslpviw 샘플: 111-36-01 → LV4=111-36, LV5=1 (마지막 마디 선행 0 제거), LV_END=5
  if (parts.length >= 3) {
    const last = parts[parts.length - 1];
    const asNum = Number(last);
    return {
      ...base,
      lv4: `${parts[0]}-${parts[1]}`,
      lv5: Number.isFinite(asNum) ? String(asNum) : last,
      lvEnd: '5',
    };
  }
  return { ...base, lv4: s, lv5: '', lvEnd: s ? '4' : '' };
}

function resolveAcc(accCd, acctMap, accMaster, missing) {
  const raw = String(accCd || '').trim();
  const aliased = ACC_ALIAS[raw] || raw;
  const remap = acctMap instanceof Map ? acctMap.get(aliased) || acctMap.get(raw) : null;
  const mappedCd = remap?.acctCd || aliased;
  const a = accMaster instanceof Map ? accMaster.get(mappedCd) : null;
  if (a) {
    const dcr = String(a.ACC_DCR_GB || '').trim() || '1';
    return { accCd: mappedCd, ...accLevelsFromUpp(a.ACC_UPP_CD), dcr, found: true };
  }
  const fb = ACC_FALLBACK[mappedCd];
  if (fb) {
    missing.add(`${mappedCd} (fallback ${fb.upp})`);
    return { accCd: mappedCd, ...accLevelsFromUpp(fb.upp), dcr: fb.dcr, found: false };
  }
  if (mappedCd) missing.add(mappedCd);
  return { accCd: mappedCd, ...accLevelsFromUpp(''), dcr: '', found: false };
}

function firstNonEmpty(lines, getter) {
  for (const r of lines) {
    const v = getter(r);
    if (!emptyVal(v)) return String(v).trim();
  }
  return '';
}

export async function convertJournalRows(sourceRows, { tenantCode, acctMap, accMaster, onProgress } = {}) {
  const codeMap = acctMap instanceof Map ? acctMap : store.acctMap;
  let master = accMaster instanceof Map ? accMaster : store.accMaster;
  if (!master?.size) {
    master = loadDefaultAccMaster();
    if (!store.accMaster.size) applyAccMasterMap(master, 'aaccmstEnt200(default)');
  }
  const entCd = String(tenantCode || store.tenantCode || MIG_ENT_CD).trim() || MIG_ENT_CD;
  const missingAcc = new Set();
  const groups = new Map();

  for (const src of sourceRows || []) {
    const ymd = ymdCompact(src.slipDate);
    const yy = ymd.slice(0, 4) || String(src.slipDate || '').slice(0, 4);
    const isuSq = String(src.slipNo || '').trim();
    const k = [entCd, yy, ymd, isuSq].join('|');
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(src);
  }

  const keys = [...groups.keys()].sort((a, b) => {
    const [ea, ya, da, sa] = a.split('|');
    const [eb, yb, db, sb] = b.split('|');
    if (ea !== eb) return String(ea).localeCompare(String(eb));
    if (ya !== yb) return String(ya).localeCompare(String(yb));
    if (da !== db) return String(da).localeCompare(String(db));
    return Number(sa) - Number(sb);
  });

  const masters = [];
  const details = [];
  const views = [];
  const unbalancedRows = [];
  const typ = { '1': 0, '2': 0, '3': 0 };
  const insDt = new Date().toISOString().replace('T', ' ').slice(0, 19);
  let mappedCnt = 0;
  let totalDr = 0;
  let totalCr = 0;
  let slpSeq = 0;
  let seqYy = '';

  for (let i = 0; i < keys.length; i += 1) {
    const arr = groups.get(keys[i]).slice().sort((a, b) => padLineNo(a.lineNo) - padLineNo(b.lineNo));
    const header = arr[0];
    const ymd = ymdCompact(header.slipDate);
    const yy = ymd.slice(0, 4);
    if (yy !== seqYy) {
      slpSeq = 0;
      seqYy = yy;
    }
    slpSeq += 1;
    const typCd = slpTypOf(arr);
    typ[typCd] += 1;

    let dr = 0;
    let cr = 0;
    for (const r of arr) {
      dr += toAmt(r.drAmt);
      cr += toAmt(r.crAmt);
    }
    const diff = dr - cr;
    if (Math.abs(diff) > 0.5) {
      unbalancedRows.push({
        ENT_CD: entCd,
        SLP_YY: yy,
        SLP_SEQ: slpSeq,
        SLP_YMD: ymd,
        NO_DOCU: `${ymd}${String(header.slipNo || '').trim().padStart(4, '0')}`,
        DTL_AMT1: dr,
        DTL_AMT2: cr,
        DIFF: diff,
      });
    }
    totalDr += dr;
    totalCr += cr;
    const slpAmt = arr.reduce((s, r) => s + Math.abs(toAmt(r.drAmt) + toAmt(r.crAmt)), 0) / 2;
    const bizCd = firstNonEmpty(arr, (r) => r.projectCd);
    const bizNm =
      firstNonEmpty(
        arr.filter((r) => String(r.projectCd || '').trim() === bizCd),
        (r) => r.projectNm
      ) || firstNonEmpty(arr, (r) => r.projectNm);
    const ttl = firstNonEmpty(arr, (r) => r.isuDoc) || firstNonEmpty(arr, (r) => r.remark);
    const isuSq = String(header.slipNo || '').trim().padStart(4, '0');
    const noDocu = ymd + isuSq;
    const cnfDt = ymd.length === 8 ? `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)} 00:00:00` : '';
    const mm = ymd.length === 8 ? String(Number(ymd.slice(4, 6))) : '';
    const dpt = header.deptCd || '';
    const emp = header.empCd || '';
    const grpSeq = header.resolveNo || '';

    masters.push({
      ENT_CD: entCd,
      SLP_YY: yy,
      SLP_SEQ: slpSeq,
      SLP_YMD: ymd,
      SLP_TTL: ttl,
      SLP_TYP: typCd,
      BUD_DPT: dpt,
      ACC_UNT: ACC_UNT,
      BIZ_CD: bizCd,
      DPT_CD: dpt,
      EMP_NO: emp,
      SLP_RMK: bizNm,
      SLP_AMT: slpAmt,
      GRP_SEQ: grpSeq,
      CNF_YN: 'Y',
      CNF_YMD: ymd,
      CNF_DT: cnfDt,
      CNF_USR: emp || INS_USR,
      SLP_GB,
      NO_DOCU: noDocu,
      INS_USR,
      INS_DT: insDt,
    });

    for (const r of arr) {
      const remap = codeMap.get(r.acctCd);
      if (remap) mappedCnt += 1;
      const acc = resolveAcc(r.acctCd, codeMap, master, missingAcc);
      const am = toAmt(r.drAmt) + toAmt(r.crAmt);
      const dc = String(r.dcType || '').trim();
      const isDr = dc === '4' || dc === '2' ? false : dc === '3' || dc === '1' ? true : toAmt(r.drAmt) > 0;
      const amt1 = isDr ? am : 0;
      const amt2 = isDr ? 0 : am;
      const hasVat = !emptyVal(r.taxFg) || toAmt(r.vatAm) !== 0 || toAmt(r.supAm) !== 0;
      const ln = padLineNo(r.lineNo) || 1;
      const dtlSeq = ln * 5;

      details.push({
        ENT_CD: entCd,
        SLP_YY: yy,
        SLP_SEQ: slpSeq,
        DTL_SEQ: dtlSeq,
        ACC_CD: acc.accCd,
        DTL_AMT1: amt1,
        DTL_AMT2: amt2,
        DTL_DESC: r.remark || '',
        CUS_NMS: r.vendorNm || '',
        CUS_CD: bizNoDigits(r.bizNo),
        DTL_TYP: dtlTypOf(acc.accCd),
        DTL_RMK: r.acctNm || '',
        VAT_YN: hasVat ? '1' : '0',
        SUP_AMT: toAmt(r.supAm),
        VAT_AMT: toAmt(r.vatAm),
        VAT_GB1: r.taxFg || '',
        INS_USR,
        INS_DT: insDt,
      });

      views.push({
        ENT_CD: entCd,
        SLP_YY: yy,
        SLP_MM: mm,
        GB: GB_DANGHAE,
        SLP_SEQ: slpSeq,
        DTL_SEQ: dtlSeq,
        SLP_GB,
        RES_SEQ: '',
        RES_SUB_SEQ: '',
        RES_SEQ2: '',
        SLP_TTL: String(ttl || '').slice(0, 150),
        ACC_UNT,
        BIZ_CD: bizCd,
        BUD_DPT: dpt,
        DPT_CD: dpt,
        CNF_YMD: ymd,
        ACC_CD: acc.accCd,
        ACC_UPP_CD: acc.upp,
        LV1: acc.lv1,
        LV2: acc.lv2,
        LV3: acc.lv3,
        LV4: acc.lv4,
        LV5: acc.lv5,
        LV_END: acc.lvEnd,
        ACC_DCR_GB: acc.dcr,
        DTL_AMT1: amt1,
        DTL_AMT2: amt2,
        CUS_NMS: r.vendorNm || '',
        CUS_CD: bizNoDigits(r.bizNo),
        DEP_NM: '',
        ACT_SEQ: '',
      });
    }

    if ((i + 1) % 80 === 0) {
      onProgress?.({ done: i + 1, total: keys.length });
      await yieldToUi();
    }
  }

  return {
    masters,
    details,
    views,
    headers: unbalancedRows,
    summary: {
      lineCnt: details.length,
      slipCnt: masters.length,
      mappedCnt,
      unbalanced: unbalancedRows.length,
      totalDr,
      totalCr,
      missingAccCnt: missingAcc.size,
      missingAcc: [...missingAcc],
      typ,
    },
  };
}

function sqlEmpty(v) {
  return v === null || v === undefined || String(v).trim() === '';
}

function sqlStr(v, maxLen) {
  if (sqlEmpty(v)) return 'NULL';
  let s = String(v).trim();
  if (maxLen && s.length > maxLen) s = s.slice(0, maxLen);
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "''")}'`;
}

function sqlNum(v) {
  if (sqlEmpty(v) && v !== 0) return '0';
  const n = Number(String(v).replace(/[,\s]/g, ''));
  return Number.isFinite(n) ? String(n) : '0';
}

function mstSqlValues(r) {
  return [
    sqlStr(r.ENT_CD, 10), sqlStr(r.SLP_YY, 4), sqlNum(r.SLP_SEQ), sqlStr(r.SLP_YMD, 8),
    sqlStr(r.SLP_TTL, 500), sqlStr(r.SLP_TYP, 1), sqlStr(r.BUD_DPT, 20), sqlStr(r.ACC_UNT, 10),
    sqlStr(r.BIZ_CD, 20), sqlStr(r.DPT_CD, 50), sqlStr(r.EMP_NO, 50), sqlStr(r.SLP_RMK, 100),
    sqlNum(r.SLP_AMT), sqlEmpty(r.GRP_SEQ) ? 'NULL' : sqlNum(r.GRP_SEQ),
    sqlStr(r.CNF_YN || 'Y', 1), sqlStr(r.CNF_YMD, 8), sqlStr(r.CNF_DT), sqlStr(r.CNF_USR, 50),
    sqlStr(r.SLP_GB, 2), sqlStr(r.NO_DOCU, 20), sqlStr(r.INS_USR, 50), 'CURRENT_TIMESTAMP',
  ];
}

function dtlSqlValues(r) {
  return [
    sqlStr(r.ENT_CD, 10), sqlStr(r.SLP_YY, 4), sqlNum(r.SLP_SEQ), sqlNum(r.DTL_SEQ),
    sqlStr(r.ACC_CD, 10), sqlNum(r.DTL_AMT1), sqlNum(r.DTL_AMT2), sqlStr(r.DTL_DESC, 500),
    sqlStr(r.CUS_NMS, 100), sqlStr(r.CUS_CD, 15), sqlStr(r.DTL_TYP, 1), sqlStr(r.DTL_RMK, 100),
    sqlStr(r.VAT_YN, 1), sqlNum(r.SUP_AMT), sqlNum(r.VAT_AMT), sqlStr(r.VAT_GB1, 10),
    sqlStr(r.INS_USR, 50), 'CURRENT_TIMESTAMP',
  ];
}

function viwSqlValues(r) {
  return [
    sqlStr(r.ENT_CD, 10), sqlStr(r.SLP_YY, 4), sqlStr(r.SLP_MM, 2), sqlStr(r.GB, 2),
    sqlNum(r.SLP_SEQ), sqlNum(r.DTL_SEQ), sqlStr(r.SLP_GB, 2),
    sqlEmpty(r.RES_SEQ) ? 'NULL' : sqlNum(r.RES_SEQ),
    sqlEmpty(r.RES_SUB_SEQ) ? 'NULL' : sqlNum(r.RES_SUB_SEQ),
    sqlEmpty(r.RES_SEQ2) ? 'NULL' : sqlNum(r.RES_SEQ2),
    sqlStr(r.SLP_TTL, 150), sqlStr(r.ACC_UNT, 10), sqlStr(r.BIZ_CD, 20),
    sqlStr(r.BUD_DPT, 20), sqlStr(r.DPT_CD, 20), sqlStr(r.CNF_YMD, 8),
    sqlStr(r.ACC_CD, 10), sqlStr(r.ACC_UPP_CD, 10), sqlStr(r.LV1, 1), sqlStr(r.LV2, 2),
    sqlStr(r.LV3, 3), sqlStr(r.LV4, 6), sqlStr(r.LV5, 8), sqlStr(r.LV_END || '4', 1),
    sqlStr(r.ACC_DCR_GB, 1), sqlNum(r.DTL_AMT1), sqlNum(r.DTL_AMT2),
    sqlStr(r.CUS_NMS, 100), sqlStr(r.CUS_CD, 15),
    sqlEmpty(r.DEP_NM) ? 'NULL' : sqlStr(r.DEP_NM, 50),
    sqlEmpty(r.ACT_SEQ) ? 'NULL' : sqlNum(r.ACT_SEQ),
  ];
}

function toInsertBatches(table, cols, valueRows) {
  const chunks = [];
  for (let i = 0; i < valueRows.length; i += SQL_BATCH) {
    const part = valueRows.slice(i, i + SQL_BATCH);
    const head = `INSERT INTO ${table} (\n  ${cols.join(', ')}\n) VALUES\n`;
    const lines = part.map((vals, j) => `  (${vals.join(', ')})${j === part.length - 1 ? ';' : ','}`);
    chunks.push(head + lines.join('\n') + '\n');
  }
  return chunks.join('\n');
}

function sqlHeaderComment(converted) {
  const s = converted.summary || {};
  const years = [...new Set((converted.masters || []).map((m) => String(m.SLP_YY || '')))].filter(Boolean).sort();
  return `-- =============================================================================
-- G20 분개장 → ERP 전표 INSERT  (aslpmst → aslpdtl → aslpviw)
-- ENT_CD=${MIG_ENT_CD}  대상연도 ${years.join(', ') || `${MIG_YEAR_FROM}~${MIG_YEAR_TO}`}
-- 원본: ${store.fileName || ''}
-- 계정마스터: ${store.accMasterSource || 'aaccmstEnt200(default)'} (${store.accMaster?.size || 0}건)
-- 생성: ${new Date().toISOString()}
-- aslpmst ${s.slipCnt || 0} / aslpdtl ${s.lineCnt || 0} / aslpviw ${(converted.views || []).length}
-- 차변 ${s.totalDr || 0}  대변 ${s.totalCr || 0}  차대불일치 ${s.unbalanced || 0}  계정미매핑 ${s.missingAccCnt || 0}
-- SLP_SEQ는 연도별 1부터. DTL_SEQ=LN_SQ*5. aslpviw.GB='1'(당해). INS_USR='G20_MIG'
-- aslpviw.ACC_UPP_CD/LV*  <= aaccmst 내부코드 분해 (예: 430-31 → 4/43/430/430-31, LV_END=4)
-- =============================================================================
SET NAMES utf8mb4;
START TRANSACTION;

`;
}

export async function buildConvertedSql(converted, { onProgress } = {}) {
  const masters = converted.masters || [];
  const details = converted.details || [];
  const views = converted.views || [];
  const mstVals = [];
  for (let i = 0; i < masters.length; i += 1) {
    mstVals.push(mstSqlValues(masters[i]));
    if ((i + 1) % CONVERT_CHUNK === 0) {
      onProgress?.({ done: i + 1, total: masters.length + details.length + views.length });
      await yieldToUi();
    }
  }
  const dtlVals = details.map((r) => dtlSqlValues(r));
  const viwVals = views.map((r) => viwSqlValues(r));
  const sql =
    sqlHeaderComment(converted) +
    toInsertBatches('aslpmst', MST_EXPORT_COLS.map((c) => c[0]), mstVals) +
    '\n' +
    toInsertBatches('aslpdtl', DTL_EXPORT_COLS.map((c) => c[0]), dtlVals) +
    '\n' +
    toInsertBatches('aslpviw', VIW_EXPORT_COLS.map((c) => c[0]), viwVals) +
    '\nCOMMIT;\n';
  const years = [...new Set(masters.map((m) => String(m.SLP_YY || '')))].filter(Boolean).sort();
  const yyBit = years.length ? `${years[0]}${years.length > 1 ? `-${years[years.length - 1]}` : ''}` : `${MIG_YEAR_FROM}-${MIG_YEAR_TO}`;
  return { sql, fileName: `insert_aslp_ent${MIG_ENT_CD}_${yyBit}.sql` };
}

function downloadTextFile(fileName, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}

export async function downloadConvertedSql(converted, fileName, onProgress) {
  const built = await buildConvertedSql(converted, { onProgress });
  const name = String(fileName || built.fileName);
  downloadTextFile(name.endsWith('.sql') ? name : `${name}.sql`, built.sql);
  return built;
}

const MST_EXPORT_COLS = [
  'ENT_CD', 'SLP_YY', 'SLP_SEQ', 'SLP_YMD', 'SLP_TTL', 'SLP_TYP', 'BUD_DPT',
  'ACC_UNT', 'BIZ_CD', 'DPT_CD', 'EMP_NO', 'SLP_RMK', 'SLP_AMT', 'GRP_SEQ',
  'CNF_YN', 'CNF_YMD', 'CNF_DT', 'CNF_USR', 'SLP_GB', 'NO_DOCU', 'INS_USR', 'INS_DT',
].map((c) => [c, c]);

const DTL_EXPORT_COLS = [
  'ENT_CD', 'SLP_YY', 'SLP_SEQ', 'DTL_SEQ', 'ACC_CD', 'DTL_AMT1', 'DTL_AMT2',
  'DTL_DESC', 'CUS_NMS', 'CUS_CD', 'DTL_TYP', 'DTL_RMK', 'VAT_YN', 'SUP_AMT',
  'VAT_AMT', 'VAT_GB1', 'INS_USR', 'INS_DT',
].map((c) => [c, c]);

const VIW_EXPORT_COLS = [
  'ENT_CD', 'SLP_YY', 'SLP_MM', 'GB', 'SLP_SEQ', 'DTL_SEQ', 'SLP_GB',
  'RES_SEQ', 'RES_SUB_SEQ', 'RES_SEQ2', 'SLP_TTL', 'ACC_UNT', 'BIZ_CD',
  'BUD_DPT', 'DPT_CD', 'CNF_YMD', 'ACC_CD', 'ACC_UPP_CD', 'LV1', 'LV2',
  'LV3', 'LV4', 'LV5', 'LV_END', 'ACC_DCR_GB', 'DTL_AMT1', 'DTL_AMT2',
  'CUS_NMS', 'CUS_CD', 'DEP_NM', 'ACT_SEQ',
].map((c) => [c, c]);

const UNBAL_EXPORT_COLS = [
  ['ENT_CD', 'ENT_CD'],
  ['SLP_YY', 'SLP_YY'],
  ['SLP_SEQ', 'SLP_SEQ'],
  ['SLP_YMD', 'SLP_YMD'],
  ['NO_DOCU', 'NO_DOCU'],
  ['DTL_AMT1', 'DTL_AMT1'],
  ['DTL_AMT2', 'DTL_AMT2'],
  ['DIFF', 'DIFF'],
];

async function aoaFromRows(rows, cols, onProgress) {
  const aoa = [cols.map((c) => c[1])];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    aoa.push(cols.map(([key]) => r[key] ?? ''));
    if ((i + 1) % CONVERT_CHUNK === 0) {
      onProgress?.({ done: i + 1, total: rows.length });
      await yieldToUi();
    }
  }
  return aoa;
}

export async function downloadConvertedWorkbook(converted, fileName, onProgress) {
  const wb = XLSX.utils.book_new();
  const mstAoa = await aoaFromRows(converted.masters || [], MST_EXPORT_COLS, onProgress);
  const dtlAoa = await aoaFromRows(converted.details || [], DTL_EXPORT_COLS);
  const viwAoa = await aoaFromRows(converted.views || [], VIW_EXPORT_COLS);
  const s = converted.summary || {};
  const summaryAoa = [
    ['항목', '값'],
    ['aslpmst', s.slipCnt],
    ['aslpdtl', s.lineCnt],
    ['aslpviw', (converted.views || []).length],
    ['차변합계', s.totalDr],
    ['대변합계', s.totalCr],
    ['차대불일치전표', s.unbalanced],
    ['계정매핑적용', s.mappedCnt],
    ['계정미매핑', s.missingAccCnt],
    ['SLP_TYP 입금', s.typ?.['1'] ?? 0],
    ['SLP_TYP 출금', s.typ?.['2'] ?? 0],
    ['SLP_TYP 대체', s.typ?.['3'] ?? 0],
    ['원본파일', store.fileName],
    ['변환시각', new Date().toISOString()],
    ['비고', "aslpmst → aslpdtl → aslpviw(GB=1). INS_USR='G20_MIG', DTL_SEQ=LN_SQ*5"],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mstAoa), 'aslpmst');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(dtlAoa), 'aslpdtl');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(viwAoa), 'aslpviw');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryAoa), '변환요약');
  if ((converted.headers || []).length) {
    const unAoa = await aoaFromRows(converted.headers, UNBAL_EXPORT_COLS);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(unAoa), '차대불일치');
  }
  if (s.missingAccCnt) {
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([['ACC_CD'], ...(s.missingAcc || []).map((c) => [c])]),
      '계정미매핑'
    );
  }
  const name = String(fileName || 'G20분개장_ERP전표.xlsx');
  XLSX.writeFile(wb, name.endsWith('.xlsx') ? name : `${name}.xlsx`, { compression: true });
}


export function downloadG20JournalTemplate(fileName = 'G20분개장_업로드양식.xlsx') {
  const wb = XLSX.utils.book_new();
  // 시흥산업진흥원 더존 G20 분개장 출력 양식(2단 헤더 + dump 76열). 변환은 LN_SQ·DRCR_FG·ACCT_* 를 우선한다.
  const journalAoa = [
    G20_PRINT_HEADER_ROW0,
    G20_PRINT_HEADER_ROW1,
    padPrintSample(['2013/01/01', '1', '0', '', '[10301]보통예금', '1,000,000', '수입금 입금', '9002', '농협(운영재산)', '2013/01/01', '7', '1000', '진흥원 운영', '1', '4', '10301', '보통예금', '', '농협(운영재산)', '수입금 입금', 1000000, '1100', '경영기획팀']),
    padPrintSample(['2013/01/01', '1', '1,000,000', '[11100]현금', '', '0', '수입금 입금', '9002', '농협(운영재산)', '2013/01/01', '7', '1000', '진흥원 운영', '2', '3', '11100', '현금', '', '농협(운영재산)', '수입금 입금', 1000000, '1100', '경영기획팀']),
    padPrintSample(['2013/01/15', '2', '250,000', '[82900]사무용품비', '', '0', '사무실 물품 구입', '0001', '샘플거래처', '2013/01/15', '4', '1000', '진흥원 운영', '1', '3', '82900', '사무용품비', '', '샘플거래처', '사무실 물품 구입', 250000, '1100', '경영기획팀']),
    padPrintSample(['2013/01/15', '2', '0', '', '[10301]보통예금', '250,000', '사무실 물품 구입', '9002', '농협(운영재산)', '2013/01/15', '4', '1000', '진흥원 운영', '2', '4', '10301', '보통예금', '', '농협(운영재산)', '사무실 물품 구입', 250000, '1100', '경영기획팀']),
  ];
  const mapAoa = [
    ['G20계정코드', 'ERP계정코드', 'ERP계정명', '재무제표구분', '재무제표항목'],
    ['1110100', '1110100', '현금', 'ASSET', '유동자산'],
    ['4100100', '4100100', '보조금수익', 'REVENUE', '사업수익'],
    ['5100100', '5100100', '인건비', 'EXPENSE', '사업비용'],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(journalAoa), '분개장');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(mapAoa), '계정매핑');
  XLSX.writeFile(wb, fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
}

export function sourceGridColumns(hiCol) {
  const extras = (getJournalStore().sourceColDefs || []).map((d) =>
    hiCol.roTextColumn({ header: d.header, field: d.key, size: d.size || 100 })
  );
  return [
    hiCol.roNumberColumn({ header: '차변', field: 'drAmt', size: 110 }),
    hiCol.roNumberColumn({ header: '대변', field: 'crAmt', size: 110 }),
    ...extras,
  ];
}

